import { Button, Dialog, DialogContent, DialogTrigger, Flex, LoadingOrbit, Text, TextArea, TextField } from '@chatsdet/ui';
import { CheckCircle, GithubLogo } from '@phosphor-icons/react';

import type { GitHubRepository } from './PropertyCodeTab.types';

interface PropertyCodeLinkRepositoryDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  repositories: GitHubRepository[];
  isLoading: boolean;
  selectedRepo: string | null;
  onSelectRepo: (repoFullName: string) => void;
  rootDir: string;
  onRootDirChange: (rootDir: string) => void;
  agentHint: string;
  onAgentHintChange: (agentHint: string) => void;
  isPending: boolean;
  onCancel: () => void;
  onLink: () => void;
}

export function PropertyCodeLinkRepositoryDialog({
  isOpen,
  onOpenChange,
  repositories,
  isLoading,
  selectedRepo,
  onSelectRepo,
  rootDir,
  onRootDirChange,
  agentHint,
  onAgentHintChange,
  isPending,
  onCancel,
  onLink,
}: PropertyCodeLinkRepositoryDialogProps) {
  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={onOpenChange} size="2xl">
      <span />
      <Dialog
        eyebrow="New link"
        title="Link Repository"
        description="Select a repository to link to this property. Only repositories accessible via your GitHub App installation are shown."
        footer={
          <>
            <Button variant="ghost" onPress={onCancel} isDisabled={isPending}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onPress={onLink}
              isDisabled={!selectedRepo || isPending}
            >
              {isPending ? 'Linking...' : 'Link Repository'}
            </Button>
          </>
        }
      >
        <DialogContent>
          {isLoading ? (
            <Flex align="center" justify="center" className="py-4">
              <LoadingOrbit size="md" />
            </Flex>
          ) : repositories.length > 0 ? (
            <div className="space-y-5">
              <div className="overflow-hidden rounded-lg border border-border bg-card shadow-sm">
                <div className="max-h-60 divide-y divide-border overflow-y-auto">
                  {repositories.map((repo) => {
                    const isSelected = selectedRepo === repo.fullName;
                    return (
                      <button
                        key={repo.id}
                        type="button"
                        aria-pressed={isSelected}
                        className={`flex w-full items-center gap-3 px-4 py-3 text-left transition-colors ${
                          isSelected ? 'bg-[hsl(var(--accent-soft))]' : 'bg-card hover:bg-muted'
                        }`}
                        onClick={() => onSelectRepo(repo.fullName)}
                      >
                        <GithubLogo
                          size={20}
                          weight="duotone"
                          className="shrink-0 text-muted-foreground"
                        />
                        <div className="min-w-0 flex-1">
                          <div className="truncate font-medium">{repo.fullName}</div>
                          <div className="mt-0.5 text-xs text-muted-foreground">
                            {repo.private ? 'Private' : 'Public'} · {repo.defaultBranch}
                          </div>
                        </div>
                        {isSelected && (
                          <CheckCircle
                            size={20}
                            weight="fill"
                            className="shrink-0 text-primary"
                          />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
              {selectedRepo && (
                <div className="space-y-3 border-t border-border pt-4">
                  <Text
                    size="xs"
                    weight="semibold"
                    className="uppercase tracking-wider text-muted-foreground"
                  >
                    Monorepo Settings (Optional)
                  </Text>
                  <TextField
                    label="Primary App Directory"
                    value={rootDir}
                    onChange={onRootDirChange}
                    placeholder="/apps/web"
                    description="Hint for test planning agent (e.g., /apps/web, /packages/frontend)"
                  />
                  <TextArea
                    label="Agent Context Hint"
                    value={agentHint}
                    onChange={onAgentHintChange}
                    placeholder="This is a monorepo. The web app depends on /packages/api for backend and /packages/shared for types."
                    rows={2}
                    description="Optional context about repo organization and related services"
                  />
                </div>
              )}
            </div>
          ) : (
            <Text size="sm" className="py-4 text-center text-muted-foreground">
              All available repositories are already linked, or no repositories are accessible.
            </Text>
          )}
        </DialogContent>
      </Dialog>
    </DialogTrigger>
  );
}
